/**
 * Reading a stored route line back out.
 *
 * `route_polyline` is written by the organiser form and can come back in any of
 * the shapes a line is commonly stored as, depending on the column type and how
 * the public data source exposes it. All of them are accepted:
 *
 *   • GeoJSON LineString — { type, coordinates: [[lng, lat], …] }
 *   • an array of pairs  — [[lat, lng], …]  or  [{ lat, lng }, …]
 *   • WKT                — "LINESTRING(lng lat, lng lat, …)"
 *   • an encoded polyline — "_p~iF~ps|U_ulLnnqC…" (Google's algorithm, which is
 *     what most route tools export)
 *
 * Anything else, or anything that decodes to coordinates outside the real
 * bounds of the earth, returns null and draws no line. A route drawn through
 * the wrong hemisphere is worse than a map with only its two pins.
 */

import parsePoint from './geoPoint';

const MIN_POINTS = 2;

function clean(points) {
  const valid = points.filter(Boolean);
  return valid.length >= MIN_POINTS ? valid : null;
}

/**
 * Google's encoded polyline algorithm.
 *
 * Self-contained on purpose: it is thirty lines and adding a dependency to a
 * public bundle for them is a poor trade. Values are deltas, each as base-64
 * chunks of five bits with the continuation bit set on all but the last, and
 * the sign carried in the low bit.
 */
function decodePolyline(encoded) {
  const points = [];
  let index = 0, lat = 0, lng = 0;

  while (index < encoded.length) {
    let result = 0, shift = 0, byte;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      if (Number.isNaN(byte)) return null;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);

    result = 0; shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      if (Number.isNaN(byte)) return null;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);

    // Out of bounds means this was not an encoded polyline at all — some other
    // string that happens to be made of the same characters.
    const point = parsePoint({ lat: lat / 1e5, lng: lng / 1e5 });
    if (!point) return null;
    points.push(point);
  }

  return clean(points);
}

const WKT_LINE_RE = /^\s*(?:SRID=\d+;)?\s*LINESTRING\s*\(([^)]*)\)\s*$/i;

export default function parseLine(value) {
  if (!value) return null;

  if (Array.isArray(value)) {
    return clean(value.map(parsePoint));
  }

  if (typeof value === 'object') {
    if (value.type === 'LineString' && Array.isArray(value.coordinates)) {
      // GeoJSON is [lng, lat].
      return clean(value.coordinates.map(pair => parsePoint({ lat: pair?.[1], lng: pair?.[0] })));
    }
    if (Array.isArray(value.points)) return clean(value.points.map(parsePoint));
    return null;
  }

  if (typeof value !== 'string') return null;

  const wkt = WKT_LINE_RE.exec(value);
  if (wkt) {
    return clean(wkt[1].split(',').map(pair => {
      const [lng, lat] = pair.trim().split(/\s+/);
      return parsePoint({ lat, lng });
    }));
  }

  const trimmed = value.trim();
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      return parseLine(JSON.parse(trimmed));
    } catch (e) {
      return null;
    }
  }

  // "lat,lng;lat,lng"
  if (trimmed.includes(';')) {
    return clean(trimmed.split(';').map(parsePoint));
  }

  return decodePolyline(trimmed);
}
